import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Book, BookOpen, Lock, ChevronRight, Menu, X } from 'lucide-react';
import axios from 'axios';
import { getBackendUrl } from '../utils/config';

const PublicHandbookPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [handbook, setHandbook] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const API_URL = getBackendUrl();

  useEffect(() => {
    checkHandbook();
  }, []);

  const checkHandbook = async () => {
    try {
      // First try without password
      const response = await axios.get(`${API_URL}/api/public/handbook`);
      setHandbook(response.data);
      if (response.data.sections?.length > 0) {
        setSelectedSection(response.data.sections[0]);
        const firstPage = response.data.pages.find(p => p.section_id === response.data.sections[0].id);
        if (firstPage) setSelectedPage(firstPage);
      }
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        setNeedsPassword(true);
        setLoading(false);
      } else if (err.response?.status === 403) {
        setError('Dit handboek is nog niet gepubliceerd.');
        setLoading(false);
      } else {
        setError('Handboek niet gevonden.');
        setLoading(false);
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    try {
      const response = await axios.get(`${API_URL}/api/public/handbook?password=${encodeURIComponent(password)}`);
      setHandbook(response.data);
      setNeedsPassword(false);
      if (response.data.sections?.length > 0) {
        setSelectedSection(response.data.sections[0]);
        const firstPage = response.data.pages.find(p => p.section_id === response.data.sections[0].id);
        if (firstPage) setSelectedPage(firstPage);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setPasswordError('Onjuist wachtwoord. Probeer opnieuw.');
      } else {
        setPasswordError('Er is een fout opgetreden.');
      }
    }
  };

  const selectSection = (section) => {
    setSelectedSection(section);
    const firstPage = handbook.pages.find(p => p.section_id === section.id);
    setSelectedPage(firstPage || null);
    setMobileMenuOpen(false);
  };

  const selectPage = (page) => {
    setSelectedPage(page);
    setMobileMenuOpen(false);
  };

  // Render markdown-like content
  const renderContent = (content) => {
    if (!content) return <p className="text-gray-500 italic">Deze pagina heeft nog geen inhoud.</p>;
    
    return content.split('\n').map((line, i) => {
      // Bold
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Italic
      line = line.replace(/\*(.+?)\*/g, '<em>$1</em>');
      
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-3xl font-bold text-gray-900 mt-8 mb-4" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />;
      }
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-2xl font-semibold text-gray-800 mt-6 mb-3" dangerouslySetInnerHTML={{ __html: line.slice(3) }} />;
      }
      if (line.startsWith('### ')) {
        return <h3 key={i} className="text-xl font-medium text-gray-800 mt-5 mb-2" dangerouslySetInnerHTML={{ __html: line.slice(4) }} />;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="ml-6 list-disc text-gray-700" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />;
      }
      if (line.match(/^\d+\. /)) {
        return <li key={i} className="ml-6 list-decimal text-gray-700" dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\. /, '') }} />;
      }
      if (line.trim() === '') {
        return <br key={i} />;
      }
      return <p key={i} className="my-2 text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Book className="w-12 h-12 mx-auto mb-4 text-orange-600 animate-pulse" />
          <p className="text-gray-600">Handboek laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Book className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Handboek niet beschikbaar</h1>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Personeelshandboek</h1>
              <p className="text-gray-600 mt-2">Voer het wachtwoord in om toegang te krijgen</p>
            </div>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Wachtwoord</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Voer wachtwoord in"
                  className="text-center text-lg"
                  autoFocus
                />
                {passwordError && (
                  <p className="text-red-500 text-sm text-center">{passwordError}</p>
                )}
              </div>
              <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                Toegang krijgen
              </Button>
            </form>
            
            <p className="text-xs text-gray-500 text-center mt-6">
              Vraag je leidinggevende om het wachtwoord als je deze niet hebt
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const settings = handbook?.settings || {};
  const sections = handbook?.sections || [];
  const pages = handbook?.pages || [];
  const sectionPages = selectedSection ? pages.filter(p => p.section_id === selectedSection.id) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50 shadow-sm" style={{ borderTopColor: settings.theme_color || '#C2410C', borderTopWidth: 4 }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${settings.theme_color || '#C2410C'}20` }}>
                <Book className="w-5 h-5" style={{ color: settings.theme_color || '#C2410C' }} />
              </div>
              <div>
                <h1 className="font-bold text-lg text-gray-900">{settings.title || 'Personeelshandboek'}</h1>
                {settings.company_name && (
                  <p className="text-sm text-gray-500">{settings.company_name}</p>
                )}
              </div>
            </div>
            
            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
          
          {settings.subtitle && (
            <p className="text-gray-600 mt-2 text-sm">{settings.subtitle}</p>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto">
        <div className="flex">
          {/* Sidebar - Desktop */}
          <aside className={`
            fixed lg:sticky top-[73px] left-0 z-40 h-[calc(100vh-73px)] w-72 bg-white border-r overflow-y-auto
            transform transition-transform duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}>
            <nav className="p-4 space-y-6">
              {sections.map(section => {
                const sPages = pages.filter(p => p.section_id === section.id);
                const isActive = selectedSection?.id === section.id;
                
                return (
                  <div key={section.id}>
                    <button
                      className={`w-full text-left font-semibold text-sm uppercase tracking-wide flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                        isActive ? 'text-orange-700 bg-orange-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                      onClick={() => selectSection(section)}
                    >
                      <BookOpen className="w-4 h-4" />
                      {section.title}
                    </button>
                    
                    {isActive && sPages.length > 0 && (
                      <ul className="mt-2 ml-4 space-y-1">
                        {sPages.map(page => (
                          <li key={page.id}>
                            <button
                              className={`w-full text-left text-sm px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                                selectedPage?.id === page.id 
                                  ? 'text-orange-700 bg-orange-100 font-medium' 
                                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                              }`}
                              onClick={() => selectPage(page)}
                            >
                              <ChevronRight className={`w-4 h-4 transition-transform ${selectedPage?.id === page.id ? 'rotate-90' : ''}`} />
                              {page.title}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
              
              {sections.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-8">
                  Dit handboek heeft nog geen inhoud
                </p>
              )}
            </nav>
          </aside>

          {/* Mobile overlay */}
          {mobileMenuOpen && (
            <div 
              className="fixed inset-0 bg-black/20 z-30 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className="flex-1 min-h-[calc(100vh-73px)] lg:pl-0">
            <article className="max-w-3xl mx-auto px-4 py-8 lg:px-8 lg:py-12">
              {selectedPage ? (
                <>
                  {/* Breadcrumb */}
                  <div className="text-sm text-gray-500 mb-4 flex items-center gap-2">
                    <span>{selectedSection?.title}</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-gray-700">{selectedPage.title}</span>
                  </div>
                  
                  <h1 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'system-ui, sans-serif' }}>
                    {selectedPage.title}
                  </h1>
                  
                  <div className="prose prose-lg max-w-none">
                    {renderContent(selectedPage.content)}
                  </div>
                  
                  {/* Navigation */}
                  <div className="mt-12 pt-8 border-t flex justify-between">
                    {(() => {
                      const currentIndex = sectionPages.findIndex(p => p.id === selectedPage.id);
                      const prevPage = currentIndex > 0 ? sectionPages[currentIndex - 1] : null;
                      const nextPage = currentIndex < sectionPages.length - 1 ? sectionPages[currentIndex + 1] : null;
                      
                      return (
                        <>
                          {prevPage ? (
                            <Button variant="ghost" onClick={() => selectPage(prevPage)}>
                              ← {prevPage.title}
                            </Button>
                          ) : <div />}
                          {nextPage && (
                            <Button variant="ghost" onClick={() => selectPage(nextPage)}>
                              {nextPage.title} →
                            </Button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <div className="text-center py-16">
                  <Book className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">
                    {settings.title || 'Personeelshandboek'}
                  </h2>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {settings.subtitle || 'Selecteer een sectie in het menu om te beginnen'}
                  </p>
                </div>
              )}
            </article>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-6 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>{settings.company_name && `© ${new Date().getFullYear()} ${settings.company_name}`}</p>
          {settings.last_updated && (
            <p className="mt-1">Laatst bijgewerkt: {new Date(settings.last_updated).toLocaleDateString('nl-NL')}</p>
          )}
        </div>
      </footer>
    </div>
  );
};

export default PublicHandbookPage;
